import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Checkbox, Image, Progress, Tooltip } from "antd";
import Cookies from "js-cookie";
import { useCallback, useMemo, useRef, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { formatTime, formatTimeBySeconds } from "../../utils/formatTime";
import { getFileIcon } from "../../utils/SendFile";
import {
  isImageFile,
  isSupportedFileType,
  isVideoFile,
  openNotificationError,
  standardizeVolume,
} from "../../utils/Utils";
import { useGlobalDownloader } from "../../context/global/DownloadProvider";
import { useImageLoader } from "../customHook/ImageLoader";
import MediaViewerModal from "../modal/MediaViewerModal";
import { PreviewOfficeModal } from "../modal/PreviewOfficeFile/PreviewOfficeModal";
import { useMediaResource } from "../../hooks/useMediaResource";
import { RESOURCE } from "../../api/apiConstant";
import apiFactory from "../../api";

const fileStore = process.env.REACT_APP_FILE_STORE || "http://10.1.1.230:8000";

const PreviewFile = ({
  file,
  handleOpenMediaModal,
  imageUrl,
  videoImageUrl,
  openFilePreviewModal,
}) => {
  const fileName = file?.originalName || file?.fileName || "";
  const extension = fileName?.includes(".")
    ? fileName.substring(fileName.lastIndexOf(".") + 1).toLowerCase()
    : "";

  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "jfif",
    "svg",
    "ico",
    "tif",
    "tiff",
    "heic",
    "heif",
    "avif",
  ];
  const videoExtensions = [
    "mp4",
    "avi",
    "mkv",
    "webm",
    "mov",
    "quicktime",
    "flv",
    "wmv",
    "3gp",
    "m4v",
  ];

  const isImageByExt = extension && imageExtensions.includes(extension);
  const isVideoByExt = extension && videoExtensions.includes(extension);

  const isVideo =
    isVideoByExt ||
    isVideoFile(fileName) ||
    (!extension && (file?.contentType === "VIDEO" || file?.type === "VIDEO"));

  const isImage =
    isImageByExt ||
    isImageFile(fileName) ||
    (!extension && (file?.contentType === "IMAGE" || file?.type === "IMAGE"));

  const handleMediaClick = (e) => {
    e.stopPropagation();
    if (isImage || isVideo) {
      handleOpenMediaModal();
    } else if (
      openFilePreviewModal &&
      isSupportedFileType(file?.originalName || file?.fileName)
    ) {
      openFilePreviewModal();
    }
  };

  if (isVideo) {
    const videoThumb = file?.videoImgPreviewPath || videoImageUrl;
    return (
      <div
        className="bg-gray-900 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        {videoThumb ? (
          <img
            src={videoThumb}
            alt={file?.originalName || file?.fileName || "video preview"}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            {getFileIcon(file?.originalName || file?.fileName)}
          </div>
        )}
        <div className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition flex items-center justify-center pointer-events-none">
          <div className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white shadow-lg group-hover:scale-110 transition-transform">
            <FaPlayCircle size={26} />
          </div>
        </div>
      </div>
    );
  }

  if (isImage) {
    const imgSrc = file?.thumbnailPreviewPath || file?.mainPath || imageUrl;
    return (
      <div
        className="bg-gray-100 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={file?.originalName || file?.fileName || "image preview"}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            {getFileIcon(file?.originalName || file?.fileName)}
          </div>
        )}
      </div>
    );
  }

  const extensions = file?.originalName
    ?.substring(file?.originalName?.lastIndexOf(".") + 1)
    ?.toLowerCase();

  return (
    <div
      className="bg-[#f8fafc] w-full h-[145px] rounded flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-slate-100 transition select-none"
      onClick={handleMediaClick}
    >
      <div className="scale-110 transform text-gray-500">
        {getFileIcon(file?.originalName || file?.fileName)}
      </div>
      <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-200/70">
        {extensions || "FILE"}
      </span>
    </div>
  );
};

const MailStorageFile = ({ file, pickFile, pickedList, senderId }) => {
  const { languageMap } = useInfoUser();
  const { selectedConversation } = useMenuContext() || {};

  const [resourceId, setResourceId] = useState(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const mediaResource = useMediaResource();

  const [isFilePreviewModal, setIsFilePreviewModal] = useState(false);
  const [previewFileError, setPreviewFileError] = useState(false);

  const { imageUrl, loading, error } = useImageLoader(file?.resourceId);
  const { imageUrl: videoImgResourceUrl } = useImageLoader(
    file?.videoImgResourceId,
  );

  const fileRef = useRef(file);

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const legacyDownloadUrl = useMemo(() => {
    if (!file?.resourceId || file?.mainPath) return null;

    const baseUrl = (
      process.env.REACT_APP_AUTHENTICATION_API_URL || ""
    ).replace(/\/$/, "");
    const token = Cookies.get("access_token");
    const url = `${baseUrl}/${RESOURCE}/file/download/${file.resourceId}`;

    return token ? `${url}?token=${encodeURIComponent(token)}` : url;
  }, [file?.mainPath, file?.resourceId]);

  // Legacy mail files use the resource download endpoint instead of MinIO.
  const downloadResource = file?.mainPath || legacyDownloadUrl;
  const fileState = downloads[downloadResource] || {};
  const originalNameToSave = file?.originalName || "Willo-video";
  const extensions = file?.originalName
    ?.substring(file?.originalName?.lastIndexOf(".") + 1)
    ?.toLowerCase();

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(downloadResource);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(downloadResource);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(downloadResource, originalNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(downloadResource);
  };

  const fileIcon = useMemo(() => {
    return getFileIcon(file?.originalName || file?.fileName);
  }, [file?.originalName || file?.fileName]);

  const openFilePreviewModal = () => {
    if (!isSupportedFileType(file?.originalName || file?.fileName)) return;

    const pathUrl = file?.path || file?.pathUrl;

    if (!pathUrl) {
      openNotificationError(
        languageMap?.["preview.error.pathNotFound"] ??
          "Error opening file due to non-existent path",
      );
      return;
    }

    setUrlPreview(
      `${fileStore}/conversation/${file?.conversationId}${pathUrl}`,
    );

    setResourceId(file?.resourceId);

    setIsFilePreviewModal(true);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-1 items-center">
            <button
              onClick={handleResumeDownload}
              className="flex items-center"
            >
              <PlayCircleOutlined
                style={{ fontSize: "14px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined style={{ fontSize: "13px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <div className="flex gap-1 items-center">
            <button onClick={handlePauseDownload} className="flex items-center">
              <PauseOutlined className="text-[14px] text-red" />
            </button>
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined className="text-[13px] text-red" />
            </button>
          </div>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload} className="flex items-center">
            <IoMdDownload className="text-[14px] text-[#2b9853]" />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-1 items-center">
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined style={{ fontSize: "13px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload} className="flex items-center">
            <IoMdDownload className="text-[14px] text-[#2b9853]" />
          </button>
        );
    }
  }, [fileState?.status, downloadResource]);

  const chooseFile = () => {
    pickFile(file?.resourceId);
  };

  const showInfoFile = useMemo(() => {
    if (
      file?.actualContentType?.startsWith("video/") &&
      file?.videoImgResourceId
    ) {
      return <span>{formatTimeBySeconds(Math.round(file?.duration))}</span>;
    }

    return <span>{standardizeVolume(file?.volume)}</span>;
  }, [file]);

  const isPicked = useMemo(() => {
    return pickedList?.includes(file?.resourceId);
  }, [pickedList]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [file?.volume, fileState?.progress]);

  const handleOpenMediaModal = async () => {
    if (["IMAGE", "VIDEO"].includes(file?.contentType)) {
      const result = await apiFactory.conversationApi.detailResource({
        mainPath: file?.mainPath,
      });

      mediaResource?.setSelectedMedia(result?.data);
      mediaResource?.setMediaTypes([file?.contentType]);
      mediaResource?.setIsFetchData(true);
      mediaResource?.setVisible(true);
    }
  };

  return (
    <>
      <div className="flex flex-col w-full min-w-0">
        <div
          onClick={chooseFile}
          className={`w-full rounded p-2 transition-all duration-200 cursor-pointer border flex flex-col justify-between gap-1 select-none ${
            isPicked
              ? "bg-[#e2edfd] border-[#3b82f6] shadow-sm ring-1 ring-[#3b82f6]/40"
              : "bg-[#f1f4f8] hover:bg-[#e5ebf2] border-[#d8e0ea] hover:border-[#cbd5e1] hover:shadow-sm"
          }`}
        >
          {/* ===== 1. Header: Checkbox + Title + Download button ===== */}
          <div className="flex items-center justify-between gap-1 min-w-0 py-0">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <Checkbox
                onChange={() => chooseFile()}
                onClick={(e) => e.stopPropagation()}
                checked={isPicked}
                className="flex-shrink-0 scale-[0.75] origin-center"
              />
              <Tooltip
                title={file?.originalName || file?.fileName}
                placement="topLeft"
              >
                <span className="font-medium text-[10px] truncate flex-1 block leading-tight text-gray-800">
                  {file?.originalName || file?.fileName}
                </span>
              </Tooltip>
            </div>

            <div
              className="flex-shrink-0 p-0.5 rounded text-gray-600 hover:text-black hover:bg-gray-200/50 transition flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {renderDownloadButton()}
            </div>
          </div>

          {/* ===== 2. Preview File (Thumbnail / Icon) ===== */}
          <PreviewFile
            file={file}
            imageUrl={imageUrl}
            videoImageUrl={videoImgResourceUrl}
            handleOpenMediaModal={handleOpenMediaModal}
            openFilePreviewModal={openFilePreviewModal}
          />

          {/* ===== 3. Footer: Date & File size ===== */}
          <div className="flex items-center justify-between text-[9.5px] text-gray-500 min-w-0 py-0 leading-tight">
            <span className="truncate">{formatTime(file?.createdAt)}</span>
            <span className="font-medium ml-1.5 whitespace-nowrap flex-shrink-0">
              {showInfoFile}
            </span>
          </div>
        </div>

        {/* Download progress */}
        {[
          DOWNLOAD_STATUS.DOWNLOADING,
          DOWNLOAD_STATUS.PAUSED,
          DOWNLOAD_STATUS.ERROR,
        ].includes(fileState?.status) && (
          <div className="flex items-center gap-2 mt-1 px-1">
            <Progress
              className="flex-1 !m-0"
              percent={fileState?.progress}
              showInfo={false}
              strokeColor={
                fileState?.status === DOWNLOAD_STATUS.ERROR
                  ? "#ff4d4f"
                  : "#52c41a"
              }
              size="small"
            />
            <div className="text-gray-500 text-[10px] whitespace-nowrap">
              {progressRate}
            </div>
          </div>
        )}
      </div>

      {mediaResource.visible && (
        <MediaViewerModal mediaResource={mediaResource} />
      )}

      {/* Render Modal Xem Office */}
      {isFilePreviewModal && (
        <PreviewOfficeModal
          isOpen={isFilePreviewModal}
          onCancel={() => setIsFilePreviewModal(false)}
          resourceId={resourceId}
          contextId={selectedConversation?.conversationId}
          pdfPreviewResourceId={file?.pdfPreviewResourceId}
          fileType={file?.mimeType || extensions}
          extensions={extensions}
        />
      )}
    </>
  );
};

export default MailStorageFile;
